/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import unescapedText from 'I18n/utils/unescapedText';
import { OptionOrGroupType } from 'UIComponents/types/OptionTypes';
import MobileSearchBar from 'ecosystem-marketplace-core/components/storefront/MobileSearchBar';
import MobileTypeaheadResults from 'ecosystem-marketplace-core/components/mobile/MobileTypeaheadResults';
import MobilePanel from 'ecosystem-marketplace-core/js/components/mobile/MobilePanel';
import MobilePanelBody from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelBody';
import { debounce } from 'ecosystem-marketplace-core/utils/debounce';
import globalNavHeight from 'nav-meta/global-nav-height';
import CoreStorefrontHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontHeader';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
const StyledStickySearchBarWrapper = styled.div.withConfig({
  displayName: "MobileStorefrontSearchBar__StyledStickySearchBarWrapper",
  componentId: "sc-1lhfy0i-0"
})(["&&{position:fixed;z-index:9999;top:0;left:0;width:100%;height:", ";padding:5px 8px;background-image:linear-gradient( -303deg,", ",", " );}"], globalNavHeight, props => props.startGradientColor, props => props.endGradientColor);

const MobileStorefrontSearchBar = ({
  placeholder,
  searchTerm,
  onClearSearch,
  onSearchSubmit,
  onInputValueChange,
  onSelectedOptionChange,
  typeaheadResults
}) => {
  const {
    START_GRADIENT_COLOR,
    END_GRADIENT_COLOR
  } = CoreStorefrontHeader.defaultProps.customStyle;
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [mobileSearchBarFocused, setMobileSearchBarFocused] = useState(false);
  const [mobileTypeaheadOpen, setMobileTypeaheadOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [mobileSearchBarButtonFocused, setMobileSearchBarButtonFocused] = useState(false);
  const innerSearchBarRef = useRef(null);
  const outerSearchBarRef = useRef(null);
  const fetchTypeaheadResults = useCallback(searchValue => {
    onInputValueChange(SyntheticEvent(searchValue));
  }, [onInputValueChange]);
  const searchSubmit = useCallback(value => {
    setMobileTypeaheadOpen(false);
    onSearchSubmit(value);
  }, [onSearchSubmit]);
  const focusMobileSearchBar = useCallback(evt => {
    setMobileSearchBarFocused(true);
    setMobileTypeaheadOpen(true);

    if (evt.target.value && !typeaheadResults.length) {
      fetchTypeaheadResults(evt.target.value);
    }
  }, [fetchTypeaheadResults, typeaheadResults.length]);
  const cancelMobileSearch = useCallback(() => {
    setMobileSearchBarFocused(false);
    setMobileTypeaheadOpen(false);
  }, []);
  const selectMobileTypeaheadItem = useCallback(item => {
    onClearSearch();
    setMobileSearchBarFocused(false);
    setMobileTypeaheadOpen(false);
    onSelectedOptionChange({
      target: {
        value: item
      }
    });
  }, [onClearSearch, onSelectedOptionChange]);
  const onSearchUpdate = useCallback(debounce(searchValue => {
    const trimmedSearchValue = searchValue.trim();

    if (!trimmedSearchValue) {
      onClearSearch();
    }

    fetchTypeaheadResults(trimmedSearchValue);
    setLocalSearchTerm(trimmedSearchValue);
  }), []);
  const focusInnerSearchOnPanelOpen = useCallback(() => {
    const innerSearchBar = innerSearchBarRef.current;

    if (innerSearchBar) {
      const innerSearchInput = innerSearchBar.querySelector('.mobile-search-input');

      if (innerSearchInput) {
        innerSearchInput.focus();
        setMobileSearchBarButtonFocused(false);
      }
    }
  }, []);
  const focusOuterSearchOnPanelClose = useCallback(() => {
    setMobileSearchBarButtonFocused(true);
  }, []);
  const onPanelOpen = useCallback(() => {
    setScrollPosition(window.scrollY);

    window.onscroll = () => {
      window.scrollTo(0, 0);
    };
  }, []);
  const onPanelClose = useCallback(() => {
    window.onscroll = () => {};

    window.scrollTo(0, scrollPosition);
  }, [scrollPosition]);
  return /*#__PURE__*/_jsxs("div", {
    ref: outerSearchBarRef,
    children: [/*#__PURE__*/_jsx(MobileSearchBar, {
      ariaLabel: unescapedText('appMarketplace.storefront.searchSelect.searchInputPlaceholder'),
      buttonFocused: mobileSearchBarButtonFocused,
      cancelSearch: cancelMobileSearch,
      focused: false,
      inputValue: localSearchTerm,
      onClear: onClearSearch,
      onClick: focusMobileSearchBar,
      onSearchSubmit: searchSubmit,
      onSearchUpdate: onSearchUpdate,
      placeholder: placeholder,
      use: "placeholder"
    }), /*#__PURE__*/_jsx(MobilePanel, {
      onOpenStart: onPanelOpen,
      onOpenComplete: focusInnerSearchOnPanelOpen,
      onCloseStart: onPanelClose,
      onCloseComplete: focusOuterSearchOnPanelClose,
      open: mobileTypeaheadOpen,
      align: "top",
      children: /*#__PURE__*/_jsxs(MobilePanelBody, {
        ref: innerSearchBarRef,
        children: [/*#__PURE__*/_jsx(StyledStickySearchBarWrapper, {
          startGradientColor: START_GRADIENT_COLOR,
          endGradientColor: END_GRADIENT_COLOR,
          children: /*#__PURE__*/_jsx(MobileSearchBar, {
            cancelSearch: cancelMobileSearch,
            focused: mobileSearchBarFocused || mobileTypeaheadOpen,
            inputValue: localSearchTerm,
            onClear: onClearSearch,
            onSearchFocus: focusMobileSearchBar,
            onSearchSubmit: searchSubmit,
            onSearchUpdate: onSearchUpdate,
            placeholder: placeholder
          })
        }), /*#__PURE__*/_jsx(MobileTypeaheadResults, {
          options: typeaheadResults,
          onChange: selectMobileTypeaheadItem
        })]
      })
    })]
  });
};

MobileStorefrontSearchBar.propTypes = {
  onClearSearch: PropTypes.func.isRequired,
  onInputValueChange: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  onSelectedOptionChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  searchTerm: PropTypes.string,
  typeaheadResults: PropTypes.arrayOf(OptionOrGroupType).isRequired
};
export default MobileStorefrontSearchBar;